import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import {
  FileText, Search, Globe, Package, Link2, Radio,
  BarChart2, ChevronLeft, ChevronRight, Activity,
  Shield, Lock, Menu, X, Layers, GitCompare, Tag, AlertTriangle, LogOut, Bell, UserCircle, MessageSquare, LifeBuoy, Download, Clock, Database, BrainCircuit, Newspaper, BookOpen, ChevronUp, TowerControl, Sun, Moon
} from 'lucide-react';
import AdsTxtChecker from './pages/AdsTxtChecker';
import SellersJsonChecker from './pages/SellersJsonChecker';
import Crawler from './pages/Crawler';
import BundleTracker from './pages/BundleTracker';
import BundleLookup from './pages/BundleLookup';
import WatchTower from './pages/WatchTower';
import SchainValidator from './pages/SchainValidator';
import BidRequestAnalyzer from './pages/BidRequestAnalyzer';
import DiscrepancyChecker from './pages/DiscrepancyChecker';
import AppCategorySegmenter from './pages/AppCategorySegmenter';
import DiscrepancyMonitor from './pages/DiscrepancyMonitorV2';
import SharedMonitorView from './pages/SharedMonitorView';
import FetchEngine from './pages/FetchEngine';
import ReportAnalyzer from './pages/ReportAnalyzer';
import AccountPage from './pages/AccountPage';
import FeedbackPage from './pages/FeedbackPage';
import SupportPage from './pages/SupportPage';
import Dashboard from './pages/Dashboard';
import HomePage from './pages/HomePage4';
import ProductsPage from './pages/ProductsPage';
import ContactPage from './pages/ContactPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import LoginSitePage from './pages/LoginSitePage';
import SubscriptionGate from './pages/SubscriptionGate';
import IndustryPulse from './pages/IndustryPulse';
import Playbook from './pages/Playbook';
import './App.css';
// Apply saved theme immediately before render
const _savedRaw = localStorage.getItem('ignitorads-theme');
const savedTheme = (_savedRaw === 'light' || _savedRaw === 'dark') ? _savedRaw : 'dark';
document.documentElement.setAttribute('data-theme', savedTheme);
// Fonts (Space Grotesk + Space Mono) are loaded by the @import in App.css.
import { AuthProvider, useAuth } from './contexts/AuthContext';
// LoginPage now handled by LoginSitePage
import Admin from './pages/Admin';
import StagingBanner from './StagingBanner';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

const NAV_ITEMS = [
  { path: '/',             label: 'Dashboard',           icon: BarChart2,  tier: 'free' },
  { path: '/ads-txt',      label: 'Ads.txt Checker',     icon: FileText,   tier: 'free' },
  { path: '/sellers-json', label: 'Sellers.json',        icon: Search,     tier: 'pro'  },
  { path: '/crawler',      label: 'Crawler',             icon: Globe,      tier: 'pro'  },
  { path: '/bundles',      label: 'Bundle Tracker',      icon: Package,    tier: 'pro'  },
  { path: '/bundle-lookup',label: 'Bundle Lookup',       icon: Layers,     tier: 'pro'  },
  { path: '/schain',       label: 'Schain Validator',    icon: Link2,      tier: 'pro'  },
  { path: '/bid-request',  label: 'Bid req Analyzer',    icon: Radio,      tier: 'pro'  },
  { path: '/discrepancy',  label: 'Discrepancy Checker', icon: GitCompare, tier: 'pro'  },
  { path: '/app-category',  label: 'App Category',         icon: Tag,        tier: 'pro'  },
  { path: '/disc-monitor',   label: 'Discrepancy Monitor', icon: Activity,   tier: 'pro', beta: true  },
  { path: '/fetch-engine',     label: 'Fetch Engine',       icon: Database,      tier: 'pro', beta: true  },
  { path: '/report-analyzer', label: 'Report Analyzer',    icon: BrainCircuit,  tier: 'pro', beta: true  },
  { path: '/watch-tower',    label: 'Watch Tower',        icon: TowerControl,   tier: 'pro', beta: true  },
];

function ProRoute({ path, children }) {
  const { hasToolAccess } = useAuth();
  // Ads.txt is always free
  if (path === '/ads-txt') return children;
  return hasToolAccess(path) ? children : <SubscriptionGate toolPath={path} />;
}

// ─── Themes ──────────────────────────────────────────────────────────────────
const THEMES = [
  { id: 'dark',  label: 'Dark',  dot: '#FF6B3D', bg: '#15130F', desc: 'Ember dark'  },
  { id: 'light', label: 'Light', dot: '#FF5A2C', bg: '#F7F6F3', desc: 'Ember light' },
];

function useTheme() {
  const [theme, setThemeState] = useState(() => { const t = localStorage.getItem('ignitorads-theme'); return (t === 'light' || t === 'dark') ? t : 'dark'; });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('ignitorads-theme', theme);
  }, [theme]);
  function setTheme(id) {
    setThemeState(id);
    document.documentElement.setAttribute('data-theme', id);
    document.body.setAttribute('data-theme', id);
    localStorage.setItem('ignitorads-theme', id);
  }
  return { theme, setTheme };
}

function Sidebar({ collapsed, setCollapsed, user, company, logout, theme, setTheme }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}
      <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/brand/icon-192.png" alt="IgnitorAds" style={{ width: collapsed ? 32 : 36, height: collapsed ? 32 : 36, objectFit: 'contain', borderRadius: '6px', flexShrink: 0 }} />
            {!collapsed && (
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em', lineHeight: 1 }}>
                IgnitorAds
              </span>
            )}
          </div>
          <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        <div className="sidebar-status">
          {!collapsed && <span className="status-label">API Status</span>}
          <div className="status-dot online" title="API Online" />
        </div>

        <nav className="sidebar-nav">
          {/* Dashboard — always pinned on top, no section label */}
          {NAV_ITEMS.filter(i => i.path === '/').map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              end
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title={collapsed ? label : ''}
            >
              <Icon size={18} className="nav-icon" />
              {!collapsed && <span className="nav-label">{label}</span>}
            </NavLink>
          ))}

          {/* Free */}
          {!collapsed && (
            <div style={{ padding: '12px 14px 4px', fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
              Free
            </div>
          )}
          {NAV_ITEMS.filter(i => i.tier === 'free' && i.path !== '/').map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title={collapsed ? label : ''}
            >
              <Icon size={18} className="nav-icon" />
              {!collapsed && <span className="nav-label">{label}</span>}
            </NavLink>
          ))}

          {/* Tools (locked) */}
          {!collapsed && (
            <div style={{ padding: '12px 14px 4px', fontSize: '10px', color: 'var(--amber)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Lock size={9} /> Tools
            </div>
          )}
          {collapsed && <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0' }} />}
          {NAV_ITEMS.filter(i => i.tier === 'pro' && !i.beta).map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) => `nav-item nav-item-pro ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title={collapsed ? `🔒 ${label}` : ''}
            >
              <Icon size={18} className="nav-icon" />
              {!collapsed && (
                <>
                  <span className="nav-label">{label}</span>
                  <Lock size={12} style={{ marginLeft: 'auto', color: 'var(--amber)', opacity: 0.7 }} />
                </>
              )}
            </NavLink>
          ))}

          {/* Beta (locked) */}
          {!collapsed && (
            <div style={{ padding: '12px 14px 4px', fontSize: '10px', color: 'var(--purple)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Lock size={9} /> Beta
              <span style={{ fontSize: '8px', fontWeight: 800, background: 'color-mix(in srgb, var(--purple) 25%, transparent)', color: 'var(--purple)', border: '1px solid color-mix(in srgb, var(--purple) 40%, transparent)', borderRadius: '3px', padding: '1px 4px' }}>BETA</span>
            </div>
          )}
          {collapsed && <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0' }} />}
          {NAV_ITEMS.filter(i => i.tier === 'pro' && i.beta).map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) => `nav-item nav-item-pro ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title={collapsed ? `🔒 ${label}` : ''}
            >
              <Icon size={18} className="nav-icon" />
              {!collapsed && (
                <>
                  <span className="nav-label">{label}</span>
                  <Lock size={12} style={{ marginLeft: 'auto', color: 'var(--amber)', opacity: 0.7 }} />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {!collapsed && (
          <div className="sidebar-footer">
            <div style={{ marginBottom: '8px', padding: '8px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {company?.name}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email}
              </div>
            </div>

            <button
              onClick={logout}
              style={{
                width: '100%', padding: '7px', borderRadius: 'var(--radius-sm)',
                background: 'color-mix(in srgb, var(--red) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 20%, transparent)',
                color: 'var(--red)', cursor: 'pointer', fontSize: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                fontFamily: 'var(--font-mono)', marginBottom: '8px'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--red) 15%, transparent)'}
              onMouseLeave={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--red) 8%, transparent)'}
            >
              <LogOut size={12} /> Sign Out
            </button>
            <div className="version-tag">v1.0.0 · IgnitorAds AdTech Hub</div>
          </div>
        )}
        {collapsed && (
          <div style={{ padding: '8px', borderTop: '1px solid var(--border)' }}>
            <button
              onClick={logout}
              title="Sign Out"
              style={{
                width: '100%', padding: '8px', borderRadius: 'var(--radius-sm)',
                background: 'color-mix(in srgb, var(--red) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 20%, transparent)',
                color: 'var(--red)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <LogOut size={14} />
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

// ─── Nav Clock ───────────────────────────────────────────────────────────────
function NavClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const fmt = (tz) => now.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: tz
  });

  const zones = [
    { label: 'UTC',       tz: 'UTC',                  color: 'var(--cyan)'           },
    { label: 'GMT+5:30',  tz: 'Asia/Kolkata',          color: 'var(--amber)'          },
  ];

  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginRight: '4px' }}>
      {zones.map(({ label, tz, color }) => (
        <div key={tz} style={{ padding: '4px 10px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', textAlign: 'center' }}>
          <div style={{ fontSize: '8px', color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '1px' }}>{label}</div>
          <div style={{ fontSize: '12px', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', letterSpacing: '0.3px' }}>{fmt(tz)}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Bundle Alerts Panel ─────────────────────────────────────────────────────
function BundleAlertsPanel({ alerts, expanded }) {
  // openGroups: Set of keys like "2026-04-27" or "2026-04-27::themediagrid.com,4WPRHV,RESELLER"
  // openSections: Set of keys like "2026-04-27::line::app_not_found"
  const [openGroups,   setOpenGroups]   = useState(new Set());
  const [openSections, setOpenSections] = useState(new Set());
  // Lazy-loaded bundle IDs per recommendation — key: `listId::alertType::date`
  const [bundleCache, setBundleCache]   = useState({});

  async function fetchBundleIds(listId, alertType, crawledDate, cacheKey) {
    if (bundleCache[cacheKey]) return; // already loaded or loading
    setBundleCache(prev => ({ ...prev, [cacheKey]: { loading: true, bundles: [], error: null } }));
    try {
      const params = new URLSearchParams({ listId, alertType });
      if (crawledDate) params.set('crawledDate', crawledDate);
      const { data } = await axios.get(`/api/bundles/alerts/bundles?${params}`);
      setBundleCache(prev => ({ ...prev, [cacheKey]: { loading: false, bundles: data.bundles || [], error: null } }));
    } catch (e) {
      setBundleCache(prev => ({ ...prev, [cacheKey]: { loading: false, bundles: [], error: 'Failed to load' } }));
    }
  }

  function toggleGroup(key) {
    setOpenGroups(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }
  function toggleSection(key) {
    setOpenSections(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }

  function copyIds(ids) {
    navigator.clipboard.writeText(ids.join('\n')).then(
      () => toast.success(`Copied ${ids.length} bundle ID${ids.length !== 1 ? 's' : ''}`),
      () => toast.error('Copy failed')
    );
  }

  function fmtDate(iso) {
    if (!iso) return 'Unknown date';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  if (!alerts || alerts.length === 0) {
    return (
      <div style={{ padding: '28px', textAlign: 'center' }}>
        <div style={{ fontSize: '28px', marginBottom: '8px' }}>📦</div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          No bundle alerts yet. Configure DSP lines in the Bundle Tracker to enable daily crawl checks.
        </div>
      </div>
    );
  }

  // ── Group: date → recommendations + line → [app_not_found, line_missing] ──
  // All cron results (recommendations included) live under their crawl date.
  const byDate = {};
  const ensureDate = (date) => {
    if (!byDate[date]) byDate[date] = { date, recs: [], lines: {} };
    return byDate[date];
  };
  for (const a of alerts) {
    const date = a.crawled_at ? a.crawled_at.slice(0, 10) : 'unknown';
    if (a.alert_type === 'recommendation') { ensureDate(date).recs.push(a); continue; }
    const line     = a.alert_data?.lineSearched || a.alert_data?.dspName || 'Unknown Line';
    const dspName  = a.alert_data?.dspName || '';
    const fileType = a.alert_data?.fileType || 'ads.txt';
    const lineKey  = `${date}::${line}`;
    const bucket   = ensureDate(date);
    if (!bucket.lines[lineKey]) {
      bucket.lines[lineKey] = { line, dspName, fileType, lineKey, app_not_found: [], line_missing: [] };
    }
    bucket.lines[lineKey][a.alert_type]?.push(a);
  }
  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  return (
    <div>
      {/* Summary bar */}
      <div style={{ padding: '8px 14px', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
        <span>Bundle Crawl Alerts</span>
        <span>{alerts.length} total · {sortedDates.length} crawl day{sortedDates.length !== 1 ? 's' : ''}</span>
      </div>

      {sortedDates.map(dateKey => {
        const { date, lines, recs } = byDate[dateKey];
        const isDateOpen = openGroups.has(dateKey);
        const lineGroups = Object.values(lines);
        const totalAlerts = lineGroups.reduce((s, l) => s + l.app_not_found.length + l.line_missing.length, 0);

        return (
          <div key={dateKey}>
            {/* ── Date header ── */}
            <div
              onClick={() => toggleGroup(dateKey)}
              style={{ padding: '10px 14px', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-elevated)'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {fmtDate(date)}
                </span>
                {totalAlerts > 0 && <span className="badge badge-red" style={{ fontSize: '9px' }}>{totalAlerts} alert{totalAlerts !== 1 ? 's' : ''}</span>}
                {lineGroups.length > 0 && <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{lineGroups.length} line{lineGroups.length !== 1 ? 's' : ''}</span>}
                {recs.length > 0 && <span style={{ fontSize: '10px', color: 'var(--cyan)', fontWeight: 700 }}>💡 {recs.length}</span>}
              </div>
              <span style={{ fontSize: '12px', color: 'var(--cyan)' }}>{isDateOpen ? '▲' : '▼'}</span>
            </div>

            {/* ── Recommendations for this crawl day ── */}
            {isDateOpen && recs.length > 0 && (
              <div style={{ background: 'color-mix(in srgb, var(--cyan) 3%, transparent)', borderBottom: '1px solid var(--border)' }}>
                <div style={{ padding: '8px 14px 2px', fontSize: '10px', fontWeight: 700, color: 'var(--cyan)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  💡 Recommendations · {recs.length}
                </div>
                {recs.map((r, i) => {
                  const reason       = r.alert_data?.reason;
                  const listId       = r.list_id || r.alert_data?.listId;
                  const crawledDate  = r.crawled_at ? r.crawled_at.slice(0, 10) : null;
                  const cacheKey     = `${listId}::${reason}::${crawledDate}`;
                  const secKey       = `rec-${dateKey}-${i}`;
                  const isBundleOpen = openSections.has(secKey);
                  const cached       = bundleCache[cacheKey];

                  return (
                    <div key={i} style={{ padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <div style={{ fontSize: '18px', flexShrink: 0 }}>
                          {reason === 'app_not_found' ? '🚫' : '⚠️'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
                            {reason === 'app_not_found'
                              ? `${r.alert_data.bundleCount} app${r.alert_data.bundleCount !== 1 ? 's' : ''} no longer on store — "${r.list_name}"`
                              : `${r.alert_data.bundleCount} bundle${r.alert_data.bundleCount !== 1 ? 's' : ''} missing ${r.alert_data.dspName || 'DSP'} line — "${r.list_name}"`}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--amber)', lineHeight: 1.5, marginBottom: '6px' }}>
                            {r.alert_data?.suggestion}
                          </div>
                          {listId && reason && (
                            <div style={{ marginBottom: '4px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: isBundleOpen ? '6px' : 0 }}
                                onClick={() => {
                                  toggleSection(secKey);
                                  if (!openSections.has(secKey)) {
                                    fetchBundleIds(listId, reason, crawledDate, cacheKey);
                                  }
                                }}>
                                <span style={{ fontSize: '11px', color: 'var(--cyan)', fontWeight: 600 }}>
                                  {isBundleOpen ? '▲ Hide' : '▼ Show'} {r.alert_data.bundleCount} bundle ID{r.alert_data.bundleCount !== 1 ? 's' : ''}
                                </span>
                                {cached?.bundles?.length > 0 && (
                                  <button
                                    onClick={e => { e.stopPropagation(); copyIds(cached.bundles.map(b => b.bundle_id)); }}
                                    style={{ fontSize: '10px', color: 'var(--cyan)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '3px', padding: '1px 8px', cursor: 'pointer' }}>
                                    Copy all
                                  </button>
                                )}
                              </div>
                              {isBundleOpen && (
                                <div style={{ background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px', maxHeight: expanded ? '280px' : '160px', overflowY: 'auto' }}>
                                  {cached?.loading && (
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px' }}>Loading…</div>
                                  )}
                                  {cached?.error && (
                                    <div style={{ fontSize: '11px', color: 'var(--red)' }}>{cached.error}</div>
                                  )}
                                  {cached?.bundles?.length > 0 && cached.bundles.map((b, bi) => (
                                    <div key={bi} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', lineHeight: 1.8 }}>
                                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{b.bundle_id}</span>
                                      {b.app_name && (
                                        <span style={{ fontSize: '10px', color: 'var(--text-muted)', flexShrink: 0, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {b.app_name}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            {r.crawled_at ? new Date(r.crawled_at).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Line groups under this date ── */}
            {isDateOpen && lineGroups.map(lg => {
              const isLineOpen = openGroups.has(lg.lineKey);
              const lineTotal  = lg.app_not_found.length + lg.line_missing.length;

              return (
                <div key={lg.lineKey} style={{ borderBottom: '1px solid var(--border)' }}>
                  {/* Line header */}
                  <div
                    onClick={() => toggleGroup(lg.lineKey)}
                    style={{ padding: '8px 14px 8px 24px', background: 'color-mix(in srgb, var(--cyan) 3%, transparent)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--cyan) 7%, transparent)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--cyan) 3%, transparent)'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          {lg.dspName && <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--cyan)' }}>{lg.dspName}</span>}
                          <code style={{ fontSize: '10px', color: 'var(--amber)', background: 'color-mix(in srgb, var(--amber) 8%, transparent)', padding: '1px 6px', borderRadius: '3px', wordBreak: 'break-all' }}>
                            {lg.line}
                          </code>
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {lg.fileType} · {lineTotal} bundle{lineTotal !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--cyan)', flexShrink: 0, marginLeft: '8px' }}>{isLineOpen ? '▲' : '▼'}</span>
                  </div>

                  {/* ── Two sections inside each line ── */}
                  {isLineOpen && (
                    <div style={{ paddingLeft: '24px' }}>
                      {/* Section 1 — Apps not on store */}
                      {lg.app_not_found.length > 0 && (() => {
                        const secKey  = `${lg.lineKey}::app_not_found`;
                        const isOpen  = openSections.has(secKey);
                        const ids     = lg.app_not_found.map(a => a.bundle_id);
                        return (
                          <div style={{ borderTop: '1px solid var(--border)' }}>
                            <div style={{ padding: '7px 14px 7px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                              onClick={() => toggleSection(secKey)}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '13px' }}>🚫</span>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--red)' }}>Apps no longer on store</span>
                                <span className="badge badge-red" style={{ fontSize: '9px' }}>{ids.length}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <button
                                  onClick={e => { e.stopPropagation(); copyIds(ids); }}
                                  style={{ fontSize: '10px', color: 'var(--cyan)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '3px', padding: '2px 8px', cursor: 'pointer' }}>
                                  Copy all
                                </button>
                                <span style={{ fontSize: '11px', color: 'var(--cyan)' }}>{isOpen ? '▲' : '▼'}</span>
                              </div>
                            </div>
                            {isOpen && (
                              <div style={{ padding: '0 14px 10px 0' }}>
                                <div style={{ background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-secondary)', maxHeight: expanded ? '240px' : '140px', overflowY: 'auto', lineHeight: 1.8, wordBreak: 'break-all' }}>
                                  {lg.app_not_found.map((a, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span>{a.bundle_id}</span>
                                      {a.app_name && <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>{a.app_name}</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Section 2 — Missing ads.txt line */}
                      {lg.line_missing.length > 0 && (() => {
                        const secKey  = `${lg.lineKey}::line_missing`;
                        const isOpen  = openSections.has(secKey);
                        const ids     = lg.line_missing.map(a => a.bundle_id);
                        return (
                          <div style={{ borderTop: '1px solid var(--border)' }}>
                            <div style={{ padding: '7px 14px 7px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                              onClick={() => toggleSection(secKey)}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '13px' }}>⚠️</span>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--amber)' }}>Missing ads.txt line</span>
                                <span className="badge badge-amber" style={{ fontSize: '9px' }}>{ids.length}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <button
                                  onClick={e => { e.stopPropagation(); copyIds(ids); }}
                                  style={{ fontSize: '10px', color: 'var(--cyan)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '3px', padding: '2px 8px', cursor: 'pointer' }}>
                                  Copy all
                                </button>
                                <span style={{ fontSize: '11px', color: 'var(--cyan)' }}>{isOpen ? '▲' : '▼'}</span>
                              </div>
                            </div>
                            {isOpen && (
                              <div style={{ padding: '0 14px 10px 0' }}>
                                <div style={{ background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-secondary)', maxHeight: expanded ? '240px' : '140px', overflowY: 'auto', lineHeight: 1.8, wordBreak: 'break-all' }}>
                                  {lg.line_missing.map((a, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span>{a.bundle_id}</span>
                                      {a.app_name && <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>{a.app_name}</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ─── Notification Bell ───────────────────────────────────────────────────────
function NotificationBell() {
  const [open, setOpen]       = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState('reports'); // 'reports' | 'tickets' | 'bundles'
  const [reports, setReports] = useState([]);
  const [ticketNotifs, setTicketNotifs] = useState([]);
  const [ticketUnread, setTicketUnread] = useState(0);
  const [bundleAlerts, setBundleAlerts] = useState([]);
  const [bundleUnread, setBundleUnread] = useState(0);
  const [monitorAlerts, setMonitorAlerts] = useState([]);
  const [monitorUnread, setMonitorUnread] = useState(0);
  const [systemNotifs, setSystemNotifs] = useState([
    { id: 'reminder', type: 'reminder', msg: "Upload yesterday's reports to keep Discrepancy Monitor running", time: 'Daily reminder', read: false },
  ]);

  useEffect(() => {
    loadTicketNotifs();
    loadBundleAlerts();
    loadMonitorAlerts();
    const iv = setInterval(() => { loadTicketNotifs(); loadBundleAlerts(); loadMonitorAlerts(); }, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { if (open) { loadReports(); loadTicketNotifs(); loadBundleAlerts(); loadMonitorAlerts(); } }, [open]);

  async function loadReports() {
    try {
      const { data } = await axios.get('/api/account/report-history');
      setReports(data);
    } catch {}
  }

  async function loadTicketNotifs() {
    try {
      const { data } = await axios.get('/api/account/notifications');
      const notifs = data.notifications || [];
      setTicketNotifs(notifs);
      const lastChecked = parseInt(localStorage.getItem('support_notif_checked') || '0');
      const unread = notifs.filter(n => new Date(n.created_at).getTime() > lastChecked).length;
      setTicketUnread(unread);
      if (data.error) console.error('[notifications] server error:', data.error);
      console.log('[notifications] fetched', notifs.length, 'items, unread=', unread);
    } catch (err) {
      console.error('[notifications] failed:', err?.response?.status, err?.response?.data || err.message);
    }
  }

  async function loadBundleAlerts() {
    try {
      const { data } = await axios.get('/api/bundles/alerts');
      setBundleAlerts(data.alerts || []);
      setBundleUnread(data.unreadCount || 0);
    } catch { /* non-fatal */ }
  }

  async function markBundleAlertsRead() {
    try {
      await axios.post('/api/bundles/alerts/read-all');
      setBundleUnread(0);
      setBundleAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
    } catch { /* non-fatal */ }
  }

  async function loadMonitorAlerts() {
    try {
      const { data } = await axios.get('/api/disc-monitor-v2/notifications');
      setMonitorAlerts(data.notifications || []);
      setMonitorUnread(data.unreadCount || 0);
    } catch { /* non-fatal */ }
  }

  async function markMonitorAlertsRead() {
    try {
      await axios.post('/api/disc-monitor-v2/notifications/read-all');
      setMonitorUnread(0);
      setMonitorAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
    } catch { /* non-fatal */ }
  }

  async function markTicketsRead() {
    // Store current timestamp — any notification before now is "read"
    localStorage.setItem('support_notif_checked', Date.now().toString());
    setTicketUnread(0);
  }

  async function downloadReport(r) {
    if (!r.file_data) { toast.error('No file data available for this report'); return; }
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(r.file_data);
    a.download = r.file_name || `report-${r.tool}-${r.executed_at?.slice(0,10)}.csv`;
    a.click();
  }

  async function deleteReport(id) {
    try { await axios.delete(`/api/account/report-history/${id}`); loadReports(); } catch {}
  }

  const unreadSystem = systemNotifs.filter(n => !n.read).length;
  const totalUnread  = unreadSystem + ticketUnread + bundleUnread + monitorUnread;

  function fmtTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
  }

  const TOOL_ICONS = {
    'disc-monitor': '📊', crawler: '🕷', bundles: '📦',
    'sellers-json': '🔍', 'ads-txt': '📄', discrepancy: '⚖',
    'bundle-lookup': '🔎', schain: '⛓', 'bid-request': '📨', 'app-category': '📱'
  };

  const TYPE_ICONS = { ticket_reply: '💬', ticket_status: '🔄' };

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: open ? 'var(--bg-elevated)' : 'transparent',
        border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
        padding: '6px 8px', cursor: 'pointer', color: 'var(--text-primary)',
        display: 'flex', alignItems: 'center', position: 'relative'
      }}>
        <Bell size={15} style={{ color: totalUnread > 0 ? 'var(--amber)' : 'var(--text-muted)' }} />
        {totalUnread > 0 && (
          <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: 'var(--red)', color: 'var(--on-red)', fontSize: '9px', fontWeight: 800, borderRadius: '10px', padding: '1px 5px', minWidth: '16px', textAlign: 'center', lineHeight: '14px' }}>{totalUnread}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '36px', right: 0,
          width: expanded ? '780px' : '420px',
          zIndex: 999,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          transition: 'width 0.2s ease',
        }}>
          {/* Header + tabs */}
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>
                <Bell size={13} style={{ verticalAlign: 'middle', marginRight: '6px', color: 'var(--amber)' }} />
                Notifications
              </div>
              <button
                onClick={() => setExpanded(e => !e)}
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '3px 10px', cursor: 'pointer', color: 'var(--cyan)', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {expanded ? '⤡ Collapse' : '⤢ Expand'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button className={`btn btn-sm ${tab === 'reports' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '11px', padding: '3px 10px' }} onClick={() => setTab('reports')}>
                📋 Reports {reports.length > 0 && `(${reports.length})`}
              </button>
              <button className={`btn btn-sm ${tab === 'monitor' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '11px', padding: '3px 10px' }} onClick={() => { setTab('monitor'); markMonitorAlertsRead(); }}>
                📊 Monitor {monitorUnread > 0 && <span style={{ background: 'var(--red)', color: 'var(--on-red)', borderRadius: '8px', padding: '0 5px', fontSize: '9px', marginLeft: '4px' }}>{monitorUnread}</span>}
              </button>
              <button className={`btn btn-sm ${tab === 'bundles' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '11px', padding: '3px 10px' }} onClick={() => { setTab('bundles'); markBundleAlertsRead(); }}>
                📦 Bundles {bundleUnread > 0 && <span style={{ background: 'var(--red)', color: 'var(--on-red)', borderRadius: '8px', padding: '0 5px', fontSize: '9px', marginLeft: '4px' }}>{bundleUnread}</span>}
              </button>
              <button className={`btn btn-sm ${tab === 'tickets' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '11px', padding: '3px 10px' }} onClick={() => { setTab('tickets'); markTicketsRead(); }}>
                💬 Support {ticketUnread > 0 && <span style={{ background: 'var(--red)', color: 'var(--on-red)', borderRadius: '8px', padding: '0 5px', fontSize: '9px', marginLeft: '4px' }}>{ticketUnread}</span>}
              </button>
            </div>
          </div>

          <div style={{ maxHeight: expanded ? '640px' : '420px', overflowY: 'auto', transition: 'max-height 0.2s ease' }}>
            {tab === 'reports' && (
              <>
                {/* System notifications */}
                {systemNotifs.filter(n => !n.read).map(n => (
                  <div key={n.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'color-mix(in srgb, var(--amber) 4%, transparent)', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--amber)', flexShrink: 0, marginTop: '4px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-primary)', lineHeight: 1.5 }}>{n.msg}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{n.time}</div>
                    </div>
                    <button onClick={() => setSystemNotifs(p => p.filter(x => x.id !== n.id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>✕</button>
                  </div>
                ))}
                {reports.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Clock size={24} style={{ color: 'var(--text-muted)', marginBottom: '8px' }} />
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No reports yet. Reports generated across all tools will appear here.</div>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: '8px 14px', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>Recent Reports</div>
                    {reports.map(r => (
                      <div key={r.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{ fontSize: '18px', flexShrink: 0 }}>{TOOL_ICONS[r.tool] || '📋'}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.tool_label || r.tool}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', gap: '8px' }}>
                            <span><Clock size={9} style={{ verticalAlign: 'middle' }} /> {fmtTime(r.executed_at)}</span>
                            {r.row_count > 0 && <span>{r.row_count.toLocaleString()} rows</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                          {r.file_data && (
                            <button onClick={() => downloadReport(r)} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', color: 'var(--cyan)', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px' }}>
                              <Download size={10} /> CSV
                            </button>
                          )}
                          <button onClick={() => deleteReport(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }}>✕</button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}

            {tab === 'monitor' && (
              <div>
                {monitorAlerts.length === 0 ? (
                  <div style={{ padding: '28px', textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>📊</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      No monitor alerts yet. Notifications appear here when an Auto Monitor fetch fails or returns no data.
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: '8px 14px', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Discrepancy Monitor Alerts</span>
                      <span>{monitorAlerts.length} total</span>
                    </div>
                    {monitorAlerts.map(a => {
                      const icon = a.notif_type === 'token_expired' ? '🔑'
                        : a.notif_type === 'zero_rows'    ? '⚠️'
                        : a.notif_type === 'rate_limited' ? '⏱️'
                        : a.notif_type === 'api_error'    ? '🔴'
                        : '⚠️';
                      const color = a.notif_type === 'token_expired' ? 'var(--red)'
                        : a.notif_type === 'rate_limited' ? 'var(--amber)'
                        : 'var(--amber)';
                      return (
                        <div key={a.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', background: a.is_read ? undefined : 'color-mix(in srgb, var(--red) 4%, transparent)', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                          <div style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>{icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12px', fontWeight: a.is_read ? 400 : 700, color: 'var(--text-primary)', lineHeight: 1.4, marginBottom: '3px' }}>
                              {a.title}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '4px' }}>
                              {a.message}
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {a.monitor_name && <span>Monitor: <strong style={{ color: 'var(--text-secondary)' }}>{a.monitor_name}</strong></span>}
                              <span>{fmtTime(a.created_at)}</span>
                            </div>
                            {a.notif_type === 'token_expired' && (
                              <div style={{ marginTop: '5px', fontSize: '11px', color: color, fontWeight: 600 }}>
                                → Go to <strong>Partner API Configs</strong> and refresh the token for {a.party_name}
                              </div>
                            )}
                          </div>
                          {!a.is_read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--red)', flexShrink: 0, marginTop: '5px' }} />}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}

            {tab === 'bundles' && (
              <BundleAlertsPanel alerts={bundleAlerts} expanded={expanded} />
            )}

            {tab === 'tickets' && (
              <>
                {ticketNotifs.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>💬</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No support notifications yet. Replies from our team will appear here.</div>
                  </div>
                ) : (
                  ticketNotifs.map(n => (
                    <div key={n.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', background: n.is_read ? undefined : 'color-mix(in srgb, var(--cyan) 4%, transparent)', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <div style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>{TYPE_ICONS[n.type] || '🔔'}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-primary)', lineHeight: 1.5, fontWeight: n.is_read ? 400 : 600 }}>{n.message}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', gap: '8px' }}>
                          <span>{fmtTime(n.created_at)}</span>
                          {n.ticket_status && <span style={{ textTransform: 'capitalize' }}>Status: {n.ticket_status.replace('_', ' ')}</span>}
                        </div>
                      </div>
                      {!n.is_read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: '5px' }} />}
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Industry Pulse fresh-articles badge ──────────────────────────────────────
// Polls /api/news/stats every 5 min and shows a red count badge on the Pulse
// icon if there are fresh articles AND the user hasn't visited /industry-pulse
// in the last 6 hours. Visit timestamp lives in localStorage so the badge clears
// the moment the user actually opens the page.
function PulseBadge() {
  const [count, setCount] = React.useState(0);
  const location = useLocation();

  // Clear badge when user is on the Pulse page (and stamp lastSeenAt — the
  // IndustryPulse page also stamps this, but stamping here too is a safety net
  // in case the page mount is delayed).
  React.useEffect(() => {
    if (location.pathname.startsWith('/industry-pulse')) {
      try { localStorage.setItem('industryPulse:lastSeenAt', String(Date.now())); } catch {}
      setCount(0);
    }
  }, [location.pathname]);

  // Poll for fresh count
  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.get('/api/news/stats');
        if (cancelled) return;
        let lastSeen = 0;
        try { lastSeen = parseInt(localStorage.getItem('industryPulse:lastSeenAt') || '0', 10) || 0; } catch {}
        const hoursSinceVisit = (Date.now() - lastSeen) / 3600000;
        const today = data?.today || 0;
        // Show badge only if there's been activity in the last day AND user hasn't
        // visited in 6h+. Avoids nagging users who just checked the page.
        setCount(today > 0 && hoursSinceVisit > 6 ? today : 0);
      } catch {/* silent */}
    };
    check();
    const id = setInterval(check, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (count === 0) return null;
  return (
    <span style={{
      position: 'absolute', top: -5, right: -5,
      background: 'var(--red)', color: 'var(--on-red)',
      fontSize: 9, fontWeight: 700, fontFamily: 'var(--font-mono)',
      padding: '1px 5px', borderRadius: 8, minWidth: 16,
      textAlign: 'center', lineHeight: 1.3,
      boxShadow: '0 0 6px color-mix(in srgb, var(--red) 50%, transparent)',
      pointerEvents: 'none',
    }}>{count > 99 ? '99+' : count}</span>
  );
}

// ─── Support unread-reply badge ───────────────────────────────────────────────
// Polls /api/account/notifications every 2 min and shows a red count badge on
// the Support icon for admin replies the user hasn't seen. "Seen" is tracked via
// the same `support_notif_checked` localStorage timestamp the NotificationBell
// uses — so opening the bell's Support tab OR visiting /support clears both.
function SupportBadge() {
  const [count, setCount] = React.useState(0);
  const location = useLocation();

  // Clear the badge when the user is on the Support page, and stamp the same
  // timestamp the bell reads so the two stay in sync.
  React.useEffect(() => {
    if (location.pathname.startsWith('/support')) {
      try { localStorage.setItem('support_notif_checked', Date.now().toString()); } catch {}
      setCount(0);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.get('/api/account/notifications');
        if (cancelled) return;
        const notifs = data.notifications || [];
        let lastChecked = 0;
        try { lastChecked = parseInt(localStorage.getItem('support_notif_checked') || '0', 10) || 0; } catch {}
        const unread = notifs.filter(n => new Date(n.created_at).getTime() > lastChecked).length;
        setCount(unread);
      } catch {/* silent */}
    };
    check();
    const id = setInterval(check, 2 * 60 * 1000);
    // Re-check when the tab regains focus — catches replies that arrived while away
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [location.pathname]);

  if (count === 0) return null;
  return (
    <span style={{
      position: 'absolute', top: -5, right: -5,
      background: 'var(--red)', color: 'var(--on-red)',
      fontSize: 9, fontWeight: 700, fontFamily: 'var(--font-mono)',
      padding: '1px 5px', borderRadius: 8, minWidth: 16,
      textAlign: 'center', lineHeight: 1.3,
      boxShadow: '0 0 6px color-mix(in srgb, var(--red) 50%, transparent)',
      pointerEvents: 'none',
    }}>{count > 99 ? '99+' : count}</span>
  );
}

function AppContent() {
  const { user, company, loading, suspended, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const currentPage = NAV_ITEMS.find(item =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <span className="spinner" style={{ width: '32px', height: '32px', borderWidth: '3px', margin: '0 auto 12px', display: 'block' }} />
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Loading...</div>
      </div>
    </div>
  );

  if (!user) return <LoginSitePage />;

  if (suspended) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ maxWidth: '420px', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, background: 'color-mix(in srgb, var(--amber) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--amber) 30%, transparent)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <AlertTriangle size={28} style={{ color: 'var(--amber)' }} />
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 700, marginBottom: '8px', color: 'var(--text-primary)' }}>Account Suspended</div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '16px' }}>
          Your access to IgnitorAds AdTech Hub has been suspended. All your data is safe and preserved.
          Please contact us to resolve this.
        </div>
        <a href="mailto:support@ignitorads.com" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--accent)', color: 'var(--on-accent)', padding: '8px 20px', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: '13px', textDecoration: 'none' }}>
          Contact Support
        </a>
      </div>
    </div>
  );

  return (
    <div className={`app-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} data-theme={theme}>
      <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} user={user} company={company} logout={logout} theme={theme} setTheme={setTheme} />
      <main className="main-content">
        <div className="page-header">
          <div className="breadcrumb">
            <span className="breadcrumb-root" style={{ fontSize: "13px", fontWeight: 600 }}>IgnitorAds AdTech Hub</span>
            {currentPage && currentPage.path !== '/' && (
              <>
                <span className="breadcrumb-sep">/</span>
                <span className="breadcrumb-current" style={{ fontSize: "13px", fontWeight: 700 }}>{currentPage.label}</span>
                {currentPage.tier === 'pro' && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                    background: 'color-mix(in srgb, var(--amber) 10%, transparent)', color: 'var(--amber)',
                    border: '1px solid color-mix(in srgb, var(--amber) 25%, transparent)',
                    borderRadius: '3px', padding: '1px 6px',
                    fontSize: '10px', fontWeight: 600, marginLeft: '6px'
                  }}>
                    <Lock size={9} /> PRO
                  </span>
                )}
              </>
            )}
          </div>
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <NavClock />
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '6px 8px', display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'var(--text-secondary)' }}
            >
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <NotificationBell />
            {[
              { to: '/industry-pulse', icon: Newspaper, title: 'Industry Pulse', color: 'var(--green)' },
              { to: '/playbook', icon: BookOpen, title: 'Playbook', color: 'var(--cyan)' },
              { to: '/support', icon: LifeBuoy, title: 'Support', color: 'var(--amber)' },
              { to: '/feedback', icon: MessageSquare, title: 'Feedback', color: 'var(--purple)' },
              { to: '/account', icon: UserCircle, title: 'Account Info', color: 'var(--cyan)' },
            ].map(({ to, icon: Icon, title, color }) => (
              <NavLink key={to} to={to} title={title} style={({ isActive }) => ({
                background: isActive ? 'var(--bg-elevated)' : 'transparent',
                border: `1px solid ${isActive ? color : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)', padding: '6px 8px',
                display: 'flex', alignItems: 'center', cursor: 'pointer', textDecoration: 'none',
                position: 'relative',
              })}>
                <Icon size={15} style={{ color }} />
                {to === '/industry-pulse' && <PulseBadge />}
                {to === '/support' && <SupportBadge />}
              </NavLink>
            ))}
            <div className="live-indicator">
              <Activity size={12} />
              <span>Live</span>
            </div>
          </div>
        </div>

        <BackToTop />
        <div className="page-content">
          <Routes>
            <Route path="/"              element={<Dashboard />} />
            <Route path="/dashboard"      element={<Dashboard />} />
            <Route path="/ads-txt"       element={<AdsTxtChecker />} />
            <Route path="/industry-pulse" element={<IndustryPulse />} />
            <Route path="/playbook"        element={<Playbook />} />
            <Route path="/playbook/:slug"  element={<Playbook />} />
            <Route path="/sellers-json"  element={<ProRoute path="/sellers-json"><SellersJsonChecker /></ProRoute>} />
            <Route path="/crawler"       element={<ProRoute path="/crawler"><Crawler /></ProRoute>} />
            <Route path="/bundles"       element={<ProRoute path="/bundles"><BundleTracker /></ProRoute>} />
            <Route path="/bundle-lookup" element={<ProRoute path="/bundle-lookup"><BundleLookup /></ProRoute>} />
            <Route path="/schain"        element={<ProRoute path="/schain"><SchainValidator /></ProRoute>} />
            <Route path="/bid-request"   element={<ProRoute path="/bid-request"><BidRequestAnalyzer /></ProRoute>} />
            <Route path="/discrepancy"   element={<ProRoute path="/discrepancy"><DiscrepancyChecker /></ProRoute>} />
            <Route path="/app-category"  element={<ProRoute path="/app-category"><AppCategorySegmenter /></ProRoute>} />
            <Route path="/disc-monitor"   element={<ProRoute path="/disc-monitor"><DiscrepancyMonitor /></ProRoute>} />
            <Route path="/fetch-engine"   element={<ProRoute path="/fetch-engine"><FetchEngine /></ProRoute>} />
            <Route path="/report-analyzer" element={<ProRoute path="/report-analyzer"><ReportAnalyzer /></ProRoute>} />
            <Route path="/watch-tower"    element={<ProRoute path="/watch-tower"><WatchTower /></ProRoute>} />
            <Route path="/account"        element={<AccountPage />} />
            <Route path="/feedback"       element={<FeedbackPage />} />
            <Route path="/support"        element={<SupportPage />} />
          </Routes>
        </div>
      </main>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'var(--bg-elevated)', color: 'var(--text-primary)',
            border: '1px solid var(--border)',
            fontFamily: 'var(--font-body)', fontSize: '13px'
          }
        }}
      />
    </div>
  );
}

function GuestHome() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && user) navigate('/dashboard');
  }, [user, loading, navigate]);
  if (loading) return null;
  if (user) return null;
  return <HomePage />;
}

// ── Per-route document titles (browser tabs + GA4 page-title dimension) ─────
const ROUTE_TITLES = {
  '/':                'Site Homepage',
  '/products':        'Site Products page',
  '/contact':         'Site Contact us page',
  '/login':           'Login · IgnitorAds',
  '/privacy':         'Privacy Policy · IgnitorAds',
  '/terms':           'Terms · IgnitorAds',
  '/dashboard':       'Dashboard · IgnitorAds',
  '/ads-txt':         'Ads.txt Checker · IgnitorAds',
  '/sellers-json':    'Sellers.json · IgnitorAds',
  '/crawler':         'Ads.txt Crawler · IgnitorAds',
  '/bundles':         'Bundle Tracker · IgnitorAds',
  '/bundle-lookup':   'Bundle Lookup · IgnitorAds',
  '/schain':          'Schain Validator · IgnitorAds',
  '/bid-request':     'Bid Request Analyzer · IgnitorAds',
  '/discrepancy':     'Discrepancy Checker · IgnitorAds',
  '/disc-monitor':    'Discrepancy Monitor · IgnitorAds',
  '/app-category':    'App Category · IgnitorAds',
  '/fetch-engine':    'Fetch Engine · IgnitorAds',
  '/report-analyzer': 'Report Analyzer · IgnitorAds',
  '/watch-tower': 'Watch Tower · IgnitorAds',
  '/industry-pulse':  'Industry Pulse · IgnitorAds',
  '/playbook':        'Playbook · IgnitorAds',
  '/account':         'Account · IgnitorAds',
  '/support':         'Support · IgnitorAds',
  '/feedback':        'Feedback · IgnitorAds',
  '/admin':           'Admin · IgnitorAds',
};

function RouteTitles() {
  const { pathname } = useLocation();
  useEffect(() => {
    const exact = ROUTE_TITLES[pathname];
    const prefixKey = !exact && Object.keys(ROUTE_TITLES)
      .filter(p => p !== '/')
      .find(p => pathname.startsWith(p + '/'));
    document.title = exact
      || (prefixKey && ROUTE_TITLES[prefixKey])
      || (pathname.startsWith('/share/') ? 'Shared Monitor · IgnitorAds' : 'IgnitorAds');
  }, [pathname]);
  return null;
}

// ── Back to top — appears after one viewport of scrolling on dashboard pages ─
function BackToTop() {
  const [target, setTarget] = useState(null);
  const { pathname } = useLocation();

  // Re-evaluate on navigation (each page keeps its own scroll position)
  useEffect(() => {
    const el = document.querySelector('.page-content');
    if (el && el.scrollTop > el.clientHeight) setTarget(el);
    else setTarget(null);
  }, [pathname]);

  useEffect(() => {
    function onScroll(e) {
      const el = e.target === document
        ? (document.scrollingElement || document.documentElement)
        : e.target;
      if (!el || typeof el.scrollTop !== 'number' || !el.clientHeight) return;
      // Only react to page-sized scrollers — ignore small panels and tables
      if (el.clientHeight < window.innerHeight * 0.6) return;
      if (el.scrollTop > el.clientHeight) setTarget(el);
      else setTarget(prev => (prev === el ? null : prev));
    }
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  if (!target) return null;
  return (
    <button className="back-to-top" aria-label="Back to top" title="Back to top"
      onClick={() => target.scrollTo({ top: 0, behavior: 'smooth' })}>
      <ChevronUp size={18} />
    </button>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <StagingBanner />
        <RouteTitles />
        <Routes>
          <Route path="/admin/*" element={<Admin />} />
          <Route path="/login" element={<LoginSitePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          {/* Public read-only shared monitor view — no auth, no shell, no nav */}
          <Route path="/share/m/:token" element={<SharedMonitorView />} />
          <Route path="/" element={<GuestHome />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
        <Analytics />
        <SpeedInsights />
      </AuthProvider>
    </Router>
  );
}
